import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import {
  HGCollection,
  HGCollectionCreate,
  HGCollectionInfo,
  HGCollectionUpdate,
  HGConnection,
  HGCollectionProviderUpdate,
  HGUser,
  HGMe,
  HGAnyRecordAPI,
  HGAnyRecordSerialized,
  HGIntervalAPI,
  HGMediaLimits,
  HGRecord,
  HGRecordMedia,
  HGNicknameAvailability,
  HGNicknameStatus,
  HGProfile,
  HGProfileUpdate,
  HGAdminUserCreate,
  HGAdminUserDetail,
  HGAdminUserList,
  HGAdminUserPatch,
  HGExportRequest,
  HGExportResult,
  HGImportRequest,
  HGImportResult,
} from "../common/types";
import { API_URL } from "../common/constants";
import { apiRecordToSerialized } from "../common/record";
import { compareCollections } from "../common/collectionTree";
import { reportUnauthorized } from "./errors";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  credentials: "include",
});

// A 401 means the session is gone, whichever endpoint found out first; see
// reportUnauthorized. Everything else is left to the call site.
const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) reportUnauthorized();
  return result;
};

export const hgApi = createApi({
  reducerPath: "api",
  baseQuery,
  tagTypes: [
    "HGCollection",
    "HGDBCounts",
    "HGMe",
    "HGUser",
    "HGSubscriptions",
    "HGBuiltinUsers",
    "HGConnection",
    "HGRecord",
    "HGRecordMedia",
    "HGAdminUser",
  ],
  endpoints: (builder) => ({
    getMe: builder.query<HGMe, void>({
      providesTags: ["HGMe"],
      query: () => {
        return {
          url: "me/",
          method: "GET",
        };
      },
    }),
    refreshMe: builder.mutation<void, void>({
      invalidatesTags: ["HGMe"],
      query: () => {
        return {
          url: "me/?refresh=true",
          method: "GET",
        };
      },
    }),
    getUser: builder.query<HGUser, string>({
      providesTags: ["HGUser"],
      query: (userID) => {
        return {
          url: `user/${userID}`,
          method: "GET",
        };
      },
    }),
    getSubscriptions: builder.query<HGUser[], void>({
      providesTags: ["HGSubscriptions"],
      query: () => {
        return {
          url: "subscriptions/",
          method: "GET",
        };
      },
    }),
    // The admin-curated list a signed-out visitor switches between, in place
    // of the subscriptions they don't have. Public: no session required, and
    // the same answer for everyone.
    getBuiltinUsers: builder.query<HGUser[], void>({
      providesTags: ["HGBuiltinUsers"],
      query: () => {
        return {
          url: "builtin-users",
          method: "GET",
        };
      },
    }),
    subscribe: builder.mutation<void, string>({
      invalidatesTags: ["HGSubscriptions"],
      query: (userID) => {
        return {
          url: `subscriptions/`,
          method: "POST",
          body: { user_id: userID },
        };
      },
    }),
    unsubscribe: builder.mutation<void, string>({
      invalidatesTags: ["HGSubscriptions"],
      query: (userID) => {
        return {
          url: `subscriptions/${userID}`,
          method: "DELETE",
        };
      },
    }),
    getCollections: builder.query<HGCollection[], string | undefined>({
      providesTags: ["HGCollection"],
      query: (userID?: string) => {
        return {
          url: "collections",
          method: "GET",
          params: userID ? { user_id: userID } : undefined,
        };
      },
      transformResponse: (response: HGCollection[]) => {
        return response
          .map((collection) => {
            return {
              ...collection,
              title: collection.is_default
                ? "Without collection"
                : collection.title,
            };
          })
          .sort(compareCollections);
      },
    }),
    /**
     * `collection id → earliest signed start second` of each collection's own
     * records (title record included), as a decimal string — seconds outgrow a
     * double. Collections with nothing timed are absent. Only the sidebar's
     * sort-by-time reads it; descendants are folded in client-side.
     */
    getCollectionsStartSeconds: builder.query<
      Record<string, string>,
      string | undefined
    >({
      providesTags: ["HGCollection", "HGRecord"],
      query: (userID?: string) => {
        return {
          url: "collections/start-seconds",
          method: "GET",
          params: userID ? { user_id: userID } : undefined,
        };
      },
    }),
    getDBCounts: builder.query<
      Record<string, number>,
      {
        userID?: string;
        interval: HGIntervalAPI;
        endInterval: HGIntervalAPI | null;
      }
    >({
      providesTags: ["HGDBCounts"],
      query: ({ userID, interval, endInterval }) => {
        return {
          url: `/records/count`,
          method: "POST",
          params: { user_id: userID },
          body: {
            interval: interval,
            end_interval: endInterval,
          },
        };
      },
    }),
    getRecordsTimeRange: builder.query<
      { min_second: string | null; max_second: string | null },
      { userID?: string; collectionID?: string }
    >({
      providesTags: ["HGRecord"],
      query: ({ userID, collectionID }) => {
        return {
          url: `/records/time-range`,
          method: "POST",
          params: { user_id: userID, collection_id: collectionID },
        };
      },
    }),
    /**
     * Every distinct [start, end] second pair in scope with how many records
     * share it — the raw material for bucketing records into intervals
     * client-side, where the engine lives. Grouped server-side, so the payload
     * counts distinct *dates*, not records.
     *
     * `collectionIDs` narrows to the same set the records list itself narrows
     * to for the "direct" / "children" collection scope; omitted, the server
     * uses the whole effective subtree.
     */
    getRecordSpans: builder.query<
      {
        spans: { start: string; end: string; count: number }[];
        truncated: boolean;
      },
      { userID?: string; collectionID?: string; collectionIDs?: string[] }
    >({
      providesTags: ["HGRecord"],
      query: ({ userID, collectionID, collectionIDs }) => {
        return {
          url: `/records/spans`,
          method: "POST",
          params: { user_id: userID, collection_id: collectionID },
          // The route's only body parameter, so the body is the bare array.
          // Empty rather than null for "no narrowing": the server reads both
          // the same way, and fetchBaseQuery's jsonifiable check dereferences
          // `body.toJSON`, which throws on null.
          body: collectionIDs ?? [],
        };
      },
    }),
    getCollection: builder.query<
      HGCollectionInfo,
      { userID: string; collectionID: string }
    >({
      providesTags: ["HGCollection"],
      query: ({ userID, collectionID }) => {
        return {
          url: `collections/${userID}/${collectionID}`,
          method: "GET",
        };
      },
      // The embedded title record is a whole record off the wire, era beside an
      // unsigned `n`, so it needs the same normalisation as the ones the record
      // endpoints return -- the band above the timeline reads its date.
      transformResponse: (
        response: Omit<HGCollectionInfo, "title_record"> & {
          title_record: HGAnyRecordAPI | null;
        },
      ): HGCollectionInfo => ({
        ...response,
        title_record: response.title_record
          ? apiRecordToSerialized(response.title_record)
          : null,
      }),
    }),
    createCollection: builder.mutation<{ id: string }, HGCollectionCreate>({
      invalidatesTags: ["HGCollection"],
      query: (collection) => {
        return {
          url: "collections",
          method: "POST",
          body: collection,
        };
      },
    }),
    updateCollection: builder.mutation<void, HGCollectionUpdate>({
      invalidatesTags: ["HGCollection"],
      query: (collection) => {
        return {
          url: "collections/" + collection.id,
          method: "PUT",
          body: collection,
        };
      },
    }),
    deleteCollection: builder.mutation<void, string>({
      invalidatesTags: ["HGCollection"],
      query: (id) => {
        return {
          url: `collections/${id}`,
          method: "DELETE",
        };
      },
    }),
    setCollectionFitRange: builder.mutation<
      void,
      { collectionID: string; fitRangeOnOpen: boolean }
    >({
      invalidatesTags: ["HGCollection"],
      query: ({ collectionID, fitRangeOnOpen }) => ({
        url: `collections/${collectionID}/fit-range`,
        method: "PUT",
        body: { fit_range_on_open: fitRangeOnOpen },
      }),
    }),
    setCollectionVisibility: builder.mutation<
      void,
      { collectionID: string; isPublic: boolean }
    >({
      invalidatesTags: ["HGCollection"],
      query: ({ collectionID, isPublic }) => {
        return {
          url: `collections/${collectionID}/visibility`,
          method: "PUT",
          body: {
            is_public: isPublic,
          },
        };
      },
    }),
    setCollectionPin: builder.mutation<
      void,
      { collectionID: string; isPinned: boolean }
    >({
      invalidatesTags: ["HGCollection"],
      query: ({ collectionID, isPinned }) => {
        return {
          url: `collections/${collectionID}/pin`,
          method: "PUT",
          body: {
            is_pinned: isPinned,
          },
        };
      },
    }),
    // The ids of one sidebar level, in the order they should be listed. Sent
    // whole rather than as a moved-from/moved-to pair, so the level lands
    // entirely placed however it was ordered before the first drag.
    reorderCollections: builder.mutation<void, string[]>({
      invalidatesTags: ["HGCollection"],
      query: (orderedIDs) => {
        return {
          url: `collections/order`,
          method: "PUT",
          body: orderedIDs,
        };
      },
    }),
    reorderCollectionPins: builder.mutation<void, string[]>({
      invalidatesTags: ["HGCollection"],
      query: (orderedIDs) => {
        return {
          url: `collections/pins/reorder`,
          method: "PUT",
          body: orderedIDs,
        };
      },
    }),
    // Linking a title record also removes it from the collection it now titles,
    // so a per-collection record count moves: HGDBCounts goes stale along with
    // the collection and the record itself.
    setCollectionTitleRecord: builder.mutation<
      void,
      { collectionID: string; recordID: string | null }
    >({
      invalidatesTags: ["HGCollection", "HGRecord", "HGDBCounts"],
      query: ({ collectionID, recordID }) => {
        return {
          url: `collections/${collectionID}/title-record`,
          method: "PUT",
          body: { record_id: recordID },
        };
      },
    }),
    createCollectionFromRecord: builder.mutation<{ id: string }, string>({
      invalidatesTags: ["HGCollection", "HGRecord", "HGDBCounts"],
      query: (recordID) => {
        return {
          url: `records/${recordID}/collection`,
          method: "POST",
        };
      },
    }),
    addChildCollection: builder.mutation<
      void,
      { parentID: string; childIDs: string[] }
    >({
      invalidatesTags: ["HGCollection"],
      query: ({ parentID, childIDs }) => {
        return {
          url: `collections/${parentID}/children`,
          method: "POST",
          body: childIDs,
        };
      },
    }),
    removeChildCollection: builder.mutation<
      void,
      { parentID: string; childIDs: string[] }
    >({
      invalidatesTags: ["HGCollection"],
      query: ({ parentID, childIDs }) => {
        return {
          url: `collections/${parentID}/children`,
          method: "DELETE",
          body: childIDs,
        };
      },
    }),
    getNoteRecords: builder.query<
      HGRecord[],
      { userID?: string; collectionID?: string }
    >({
      providesTags: ["HGRecord"],
      query: ({ userID, collectionID }) => {
        return {
          url: `records/${userID}/${collectionID}`,
          method: "GET",
        };
      },
      // The wire sends `created`/`modified` as ISO strings; the rest of the app
      // (and `HGRecord`) expects epoch millis, the same normalisation
      // `expandRecordsMap` does for the timeline. Without it RecordsNotes sorts
      // by `string - string` → NaN and the order never changes.
      transformResponse: (response: HGRecord[]) =>
        response.map((record) => ({
          ...record,
          created: new Date(record.created).getTime(),
          modified:
            record.modified == null
              ? null
              : new Date(record.modified).getTime(),
        })),
    }),
    getRecord: builder.query<HGAnyRecordSerialized, string>({
      providesTags: ["HGRecord"],
      query: (recordID) => {
        return {
          url: `records/${recordID}`,
          method: "GET",
        };
      },
      transformResponse: (response: HGAnyRecordAPI) =>
        apiRecordToSerialized(response),
    }),
    // Backs the /{nickname}/-/{slug} record page. Both segments also accept an
    // old slug or a raw id, in which case the response carries canonical_path.
    getRecordBySlug: builder.query<
      HGAnyRecordSerialized,
      { userRef: string; recordRef: string }
    >({
      providesTags: ["HGRecord"],
      query: ({ userRef, recordRef }) => {
        return {
          url: `records/by-slug/${encodeURIComponent(
            userRef,
          )}/${encodeURIComponent(recordRef)}`,
          method: "GET",
        };
      },
      transformResponse: (response: HGAnyRecordAPI) =>
        apiRecordToSerialized(response),
    }),
    getNickname: builder.query<HGNicknameStatus, void>({
      providesTags: ["HGMe"],
      query: () => ({ url: "user/nickname", method: "GET" }),
    }),
    checkNickname: builder.query<HGNicknameAvailability, string>({
      query: (nickname) => ({
        url: "user/nickname/available",
        method: "GET",
        params: { nickname },
      }),
    }),
    setNickname: builder.mutation<HGNicknameStatus, string>({
      // Every collection link embeds the nickname, so they all go stale.
      invalidatesTags: ["HGMe", "HGUser", "HGCollection"],
      query: (nickname) => ({
        url: "user/nickname",
        method: "PUT",
        body: { nickname },
      }),
    }),
    // The three profile mutations all return the resolved profile, which the
    // caller pushes into `me` via setMeProfile. HGUser is invalidated because
    // anyone viewing this user's space reads the same name and avatar.
    updateProfile: builder.mutation<HGProfile, HGProfileUpdate>({
      invalidatesTags: ["HGUser"],
      query: (body) => ({ url: "user/profile", method: "PATCH", body }),
    }),
    uploadAvatar: builder.mutation<HGProfile, File>({
      invalidatesTags: ["HGUser"],
      query: (file) => {
        const body = new FormData();
        body.append("file", file);
        // No Content-Type header: the browser has to set the multipart
        // boundary itself.
        return { url: "user/avatar", method: "POST", body };
      },
    }),
    deleteAvatar: builder.mutation<HGProfile, void>({
      invalidatesTags: ["HGUser"],
      query: () => ({ url: "user/avatar", method: "DELETE" }),
    }),
    getRecordMedia: builder.query<HGRecordMedia[], string>({
      providesTags: (_result, _error, recordID) => [
        { type: "HGRecordMedia", id: recordID },
      ],
      query: (recordID) => {
        return {
          url: `records/${recordID}/media`,
          method: "GET",
        };
      },
    }),
    getMediaLimits: builder.query<HGMediaLimits, void>({
      query: () => {
        return {
          url: "records/media/limits",
          method: "GET",
        };
      },
    }),
    // --- external systems -------------------------------------------------
    // Connections are grants, not data: this lists them and revokes them, and
    // never carries a token. The short-lived access token a fetch needs is
    // minted by src/providers/external/tokens.ts, which keeps it in memory and
    // out of the RTK Query cache -- resetApiState() on every sign-in would
    // wipe it, and nothing should serialize a live credential anyway.
    getConnections: builder.query<HGConnection[], void>({
      providesTags: ["HGConnection"],
      query: () => ({ url: "connections", method: "GET" }),
    }),
    startAuthorization: builder.mutation<
      { url: string },
      { system_id: string; scopes: string[]; return_to?: string }
    >({
      query: (body) => ({
        url: "connections/authorize",
        method: "POST",
        body,
      }),
    }),
    deleteConnection: builder.mutation<void, string>({
      // Collections keep their provider settings and fall back to the connect
      // prompt, so the collection list has to be re-read too.
      invalidatesTags: ["HGConnection", "HGCollection"],
      query: (connectionID) => ({
        url: `connections/${connectionID}`,
        method: "DELETE",
      }),
    }),
    setCollectionProvider: builder.mutation<
      void,
      { collectionID: string } & HGCollectionProviderUpdate
    >({
      invalidatesTags: ["HGCollection"],
      query: ({ collectionID, ...body }) => ({
        url: `collections/${collectionID}/provider`,
        method: "PUT",
        body,
      }),
    }),
    getAdminUsers: builder.query<
      HGAdminUserList,
      {
        q?: string;
        limit?: number;
        offset?: number;
        // Omitted lists everyone; true/false only special accounts or the rest.
        special?: boolean;
      } | void
    >({
      providesTags: ["HGAdminUser"],
      query: (args) => {
        // fetchBaseQuery serialises an explicit `undefined` param as the
        // string "undefined", so build the params object conditionally.
        const params: Record<string, string | number> = {
          limit: args?.limit ?? 50,
          offset: args?.offset ?? 0,
        };
        if (args?.q) params.q = args.q;
        if (args?.special !== undefined) params.special = String(args.special);
        return { url: "admin/users", method: "GET", params };
      },
    }),
    getAdminUser: builder.query<HGAdminUserDetail, string>({
      providesTags: (_result, _error, id) => [{ type: "HGAdminUser", id }],
      query: (id) => ({ url: `admin/users/${id}`, method: "GET" }),
    }),
    createAdminUser: builder.mutation<HGAdminUserDetail, HGAdminUserCreate>({
      invalidatesTags: ["HGAdminUser"],
      query: (body) => ({ url: "admin/users", method: "POST", body }),
    }),
    patchAdminUser: builder.mutation<
      HGAdminUserDetail,
      { userID: string; patch: HGAdminUserPatch }
    >({
      // An admin can patch their own row (e.g. records_exempt), so HGMe goes
      // stale too -- but `me` itself comes from checkAuthorization, not RTK
      // Query, so the caller must also re-run that when userID === own id.
      // builtin_rank is patched from here as well, and the switcher reads that
      // list from its own cache entry.
      invalidatesTags: ["HGAdminUser", "HGMe", "HGBuiltinUsers"],
      query: ({ userID, patch }) => ({
        url: `admin/users/${userID}`,
        method: "PATCH",
        body: patch,
      }),
    }),
    deleteAdminUser: builder.mutation<void, string>({
      // The account goes with its collections, so anything that could list
      // it or them -- the switcher, a follow list, public collections -- is
      // stale too.
      invalidatesTags: [
        "HGAdminUser",
        "HGBuiltinUsers",
        "HGSubscriptions",
        "HGCollection",
      ],
      query: (id) => ({ url: `admin/users/${id}`, method: "DELETE" }),
    }),
    setAdminUserPassword: builder.mutation<
      void,
      { userID: string; password: string }
    >({
      invalidatesTags: ["HGAdminUser"],
      query: ({ userID, password }) => ({
        url: `admin/users/${userID}/password`,
        method: "PUT",
        body: { password },
      }),
    }),
    addAdminUserCredentials: builder.mutation<
      HGAdminUserDetail,
      { userID: string; username: string; password: string }
    >({
      invalidatesTags: ["HGAdminUser"],
      query: ({ userID, ...body }) => ({
        url: `admin/users/${userID}/credentials`,
        method: "POST",
        body,
      }),
    }),
    setAdminUserUsername: builder.mutation<
      HGAdminUserDetail,
      { userID: string; username: string }
    >({
      invalidatesTags: ["HGAdminUser"],
      query: ({ userID, username }) => ({
        url: `admin/users/${userID}/username`,
        method: "PUT",
        body: { username },
      }),
    }),
    deleteAdminUserCredentials: builder.mutation<void, string>({
      invalidatesTags: ["HGAdminUser"],
      query: (id) => ({
        url: `admin/users/${id}/credentials`,
        method: "DELETE",
      }),
    }),

    // A dry run writes nothing, so it must not invalidate anything either —
    // otherwise previewing a file would refetch the whole sidebar. The real run
    // does, and its caller also has to pulse globalReloader: the timeline's
    // records are in neither Redux nor this cache, so no tag reaches them.
    importDocument: builder.mutation<HGImportResult, HGImportRequest>({
      invalidatesTags: (_result, _error, arg) =>
        arg.dry_run ? [] : ["HGCollection", "HGRecord", "HGDBCounts"],
      query: (body) => ({ url: "import", method: "POST", body }),
    }),
    // A mutation rather than a query because export writes: it records an id
    // for every entity it emits, which is what lets the file be edited and
    // imported back onto the same records.
    exportDocument: builder.mutation<HGExportResult, HGExportRequest>({
      query: (body) => ({ url: "export", method: "POST", body }),
    }),
  }),
});

export const {
  useGetMeQuery,
  useGetUserQuery,
  useGetSubscriptionsQuery,
  useGetBuiltinUsersQuery,
  useSubscribeMutation,
  useUnsubscribeMutation,
  useRefreshMeMutation,
  useGetCollectionsQuery,
  useGetCollectionQuery,
  useGetDBCountsQuery,
  useGetRecordsTimeRangeQuery,
  useGetCollectionsStartSecondsQuery,
  useGetRecordSpansQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
  useSetCollectionVisibilityMutation,
  useSetCollectionFitRangeMutation,
  useSetCollectionPinMutation,
  useReorderCollectionsMutation,
  useReorderCollectionPinsMutation,
  useSetCollectionTitleRecordMutation,
  useCreateCollectionFromRecordMutation,
  useAddChildCollectionMutation,
  useRemoveChildCollectionMutation,
  useGetNoteRecordsQuery,
  useGetRecordQuery,
  useGetRecordBySlugQuery,
  useGetNicknameQuery,
  useLazyCheckNicknameQuery,
  useSetNicknameMutation,
  useUpdateProfileMutation,
  useUploadAvatarMutation,
  useDeleteAvatarMutation,
  useGetRecordMediaQuery,
  useGetMediaLimitsQuery,
  useGetConnectionsQuery,
  useStartAuthorizationMutation,
  useDeleteConnectionMutation,
  useSetCollectionProviderMutation,
  useGetAdminUsersQuery,
  useGetAdminUserQuery,
  useCreateAdminUserMutation,
  usePatchAdminUserMutation,
  useDeleteAdminUserMutation,
  useSetAdminUserPasswordMutation,
  useAddAdminUserCredentialsMutation,
  useSetAdminUserUsernameMutation,
  useDeleteAdminUserCredentialsMutation,
  useImportDocumentMutation,
  useExportDocumentMutation,
} = hgApi;

// export default hgApi;
